<script setup>
import router from '@/router';
import { computed, ref } from 'vue'
import TimeComponent from './TimeComponent.vue';
import { useApiStore } from '@/stores/apiStore';
import { useTranslationStore } from '@/stores/translationStore';

const apiStore = useApiStore()
const translationStore = useTranslationStore()

const props = defineProps(['item'])
const showNavOptions = ref(false)

const openWerkbon = () => {
    apiStore.v2loadOpdracht(props.item.opdrachtId);
    //router.push('/werkbon')
    router.push({ name: 'werkbon', params: { id: props.item.opdrachtId } })
}

const bgColor = computed(() => {
    if (props.item.statusUitvoeringOpdracht == 'Gestart') return 'bg-mc-secondary'
    if (props.item.statusUitvoeringOpdracht == 'Uitgevoerd') return 'bg-gray-400'
    if (props.item.statusUitvoeringOpdracht == 'Niet uitgevoerd') return 'bg-red-600'

  if (props.item.statusUitvoeringOpdracht == null) return 'bg-mc-primary'

  return 'bg-mc-primary'
})

const fullAddress = computed(() => {
    return `${props.item.opdrachtStraat} ${props.item.opdrachtHuisNr}, ${props.item.opdrachtPostcode} ${props.item.opdrachtGemeente}`.trim()
})

const toggleNavOptions = () => {
    showNavOptions.value = !showNavOptions.value
}

const openNavigation = (app) => {
    const address = encodeURIComponent(fullAddress.value)
    
    if (app === 'waze') {
        // Use the official Waze Universal Link which handles app detection automatically
        // This is the recommended approach from Waze documentation
        const wazeUrl = `https://waze.com/ul?q=${address}&navigate=yes`
        // For better reliability on tablets, we can also try the direct app scheme first
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        
        if (isMobile) {
            // Create a hidden iframe to try the app scheme without navigation issues
            const iframe = document.createElement('iframe')
            iframe.style.display = 'none'
            iframe.src = `waze://?q=${address}&navigate=yes`
            document.body.appendChild(iframe)
            
            // Clean up iframe after a short delay
            setTimeout(() => {
                document.body.removeChild(iframe)
            }, 100)
            
            // Also open the universal link as backup
            setTimeout(() => {
                window.open(wazeUrl, '_blank')
            }, 500)
        } else {
            // For desktop, just use the universal link
            window.open(wazeUrl, '_blank')
        }
    } else if (app === 'google') {
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank')
    }
    
    // Close the options after selection
    showNavOptions.value = false
}
</script>

<template>
  <button @click="openWerkbon" class="relative py-1">
    <div
      :class="[bgColor, item.swtEersteWerk ? 'border-4 border-red-600' : '']"
      class="py-6 px-4 flex flex-col gap-6 rounded-lg text-left text-sm min-h-[200px] relative"
    >
      <div class="grid grid-cols-12">
        <div>
          <span>{{ item.volgr }}</span>
        </div>
        <div class="col-span-4 flex flex-col">
          <span class="font-bold">{{ item.klantId }}</span>
          <span class="font-medium">{{ item.naam }}</span>
          <span>{{ item.opdrachtContactPersoon }}</span>
          <span>{{ item.opdrachtStraat }} {{item.opdrachtHuisNr}}, {{ item.opdrachtPostcode }} {{ item.opdrachtGemeente }}</span>
          <div @click.stop class="relative mt-2">
            <button 
              @click="toggleNavOptions"
              class="bg-mc-tertiary hover:bg-mc-secondary text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 shadow-sm transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              Navigeren
            </button>
            <!-- Navigation dropdown -->
            <div v-show="showNavOptions" class="absolute top-12 left-0 bg-white rounded-lg shadow-2xl border z-30 min-w-[200px] overflow-hidden">
              <button @click="openNavigation('waze')" class="w-full text-left px-6 py-4 text-base text-gray-700 hover:bg-gray-50 flex items-center gap-4 border-b transition-colors">
                <img src="/waze.png" alt="Waze" class="w-8 h-8">
                <span class="font-semibold text-gray-800">Waze</span>
              </button>
              <button @click="openNavigation('google')" class="w-full text-left px-6 py-4 text-base text-gray-700 hover:bg-gray-50 flex items-center gap-4 transition-colors">
                <svg class="w-7 h-7" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span class="font-semibold text-gray-800">Google Maps</span>
              </button>
            </div>
          </div>
        </div>
        <div class="col-span-2 flex flex-col">
          <span>{{ item.opdrachtTelNr }}</span>
          <span>{{ item.telNr }}</span>
        </div>
        <div class="col-span-2 flex flex-col">
          <span>{{ item.opdrachtId }}</span>
          <span>{{ item.voorkeurTijdstip }}</span>
          <span>{{ item.statusUitvoeringOpdracht }}</span>
          <span>{{ item.aantalAttachments }} {{ translationStore.t('werklijst_item.bestanden') }}</span>
        </div>
        <div @click.stop class="col-span-3 flex flex-col justify-center h-full">
            <TimeComponent :label="translationStore.t('timeclocks.vertrek_depot')" position="left" round="down" key-value="vertrek" :opdracht-id="item.opdrachtId"/>
            <TimeComponent :label="translationStore.t('timeclocks.aankomst_werf')" position="left" round="down" key-value="aankomstWerf" :opdracht-id="item.opdrachtId"/>
            <TimeComponent :label="translationStore.t('timeclocks.vertrek_werf')" position="left" round="up" key-value="vertrekWerf" :opdracht-id="item.opdrachtId"/>
            <TimeComponent :label="translationStore.t('timeclocks.aankomst_depot')" position="left" round="up" key-value="aankomst" :opdracht-id="item.opdrachtId"/>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2">
        <div>
          <h3 class="font-medium">{{ translationStore.t('werklijst_item.opdracht') }}</h3>
          <p>{{ item.opdrachtOmschKort }}</p>
        </div>
        <div>
          <h3 class="font-medium">{{ translationStore.t('werklijst_item.omschrijving') }}</h3>
          <p>{{ item.opmerking }}</p>
        </div>
      </div>
    </div>
  </button>
</template>
